<template>
    <!-- Contents -->
    <h2>Device Replacer</h2>
    <form>
        <div class="w-full mb-4">
            Select the serial number of the old point and the point you have just installed.
            <b>All data but the last hour on the destination device will be archived.</b>
            All alarm history will be tied to the new device as well as other things like scaling profile and alarm profile.
            Be sure that the unit has sent recent data in range so alarms are not created after replacement.
            Additionally, be sure that once the unit has been replace and is in range any old alarms are cleared.
        </div>

        <!-- Old Hardware or serial -->
        <div class="grid grid-cols-4">
            <label  class="col-span-1"
                    for="source_meter">
                Old Hardware/Serial
            </label>
            <div    class="col-span-2 mx-4">
                <select2    v-model:value="sourceId"
                            :close-on-select='true'
                            :options='meters'
                            name="source_meter"
                            placeholder="Select Source Meter"
                ></select2>
            </div>
            <div   class="col-span-1 text-gray-500">
                {{ sourceOldestDate }}
            </div>
        </div>

        <!-- New Hardware or serial -->
        <div class="grid grid-cols-4">
            <label  class="col-span-1 mr-4"
                    for="destination_meter">
                New Hardware/Serial
            </label>
            <div    class="col-span-2 mx-4">
                <select2    v-model:value="destinationId"
                            :close-on-select='true'
                            :options='meters'
                            name="destination_meter"
                            placeholder="Select Destination Meter"
                ></select2>
            </div>
            <div   class="col-span-1 text-gray-500">
                {{ destinationOldestDate }}
            </div>
        </div>

        <!-- Optional Ending Time -->
        <div class="grid grid-cols-4">
            <label  class="col-span-1 flex flex-col"
                    for="date">
                <span>Ending date/time of data copy</span>
                <span>(leave blank for all data)</span>

            </label>
            <div    class="col-span-2 pl-4 pt-2">
                <date-time-picker
                    v-model="endingTime"
                    :min-date="minDate"
                    :side-by-side="true"
                    :timezone="timezone"
                    class="grow"
                    format="MM/DD/YYYY HH:mm:ss"
                    placeholder="Ending Time"
                    vertical-position="top"
                    :default-current-time="false"
                ></date-time-picker>
            </div>
        </div>

        <!-- Banner after post -->
        <div    class="w-3/4 my-2">
            <!-- Success Pop-up -->
            <div>
                <notification-modal     v-model:is-open="showReplacerSuccess"
                                        :z-index="'z-10'">
                    <template   #title-content>
                        Device Replacer Queued
                    </template>

                    <p>
                        Device Replacer Queued, you will receive an email when the process is complete.
                    </p>
                    <p>
                        DO NOT request it again.
                    </p>

                    <template #button-primary-content>Sounds Good!</template>
                </notification-modal>
            </div>

            <!-- Error Banner -->
            <div    v-if="errorMessage"
                    class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                <banner-error>
                    <template   #title>
                        <span>Error</span>
                    </template>
                    <template   #content>
                        <span>{{ errorMessage }}</span>
                    </template>
                    <span>{{ errorMessage }}</span>
                </banner-error>
            </div>
        </div>

        <!-- Submit Device Replacer -->
        <div    v-if="permissions.can('use_device_replacer')"
                class="grid grid-cols-4 ">
            <div    class="flex items-center pr-4 justify-end">
                <div    class="flex flex-col text-center">
                    <span   class="font-bold">Archive existing</span>
                    <span   class="font-bold">data on destination</span>
                </div>

                <div    class="pl-4">
                    <toggle     v-model:archive-existing-data="archiveExistingData"
                    ></toggle>
                </div>
            </div>
            <div    class="col-span-2">
                <button-blue-primary  class=""
                                      @click="submitForm"
                >
                    <div    v-if="submittingDeviceReplacer"
                            class="flex items=center">
                        <span   :class="iconLoading"></span>
                        <span   class="ml-2">Attempting Device Replacement</span>

                    </div>
                    <span   v-else>
                    Replace Data
                </span>
                </button-blue-primary>
            </div>
        </div>
    </form>
</template>

<script>
import { DateTime as LuxonDatetime}  from 'luxon';
import axios from 'axios';
import _ from 'lodash';
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Loading from "./elements/loading.vue";
import Select2 from "./elements/select2.vue";
import DateTimePicker from "./Reports/DateTimePicker.vue";
import ButtonBluePrimary from "./elements/buttons/ButtonBluePrimary.vue";
import BannerSuccess from "./Banners/BannerSuccess.vue";
import BannerError from "./Banners/BannerError.vue";
import NotificationModal from "./elements/NotificationModal.vue";
import Toggle from "./elements/toggle.vue";

export default {
    name: "DeviceReplacer",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        Toggle,
        NotificationModal, BannerError, BannerSuccess, ButtonBluePrimary, DateTimePicker, Select2, Loading},
    emits: [],
    props: {
        // Props
        meters: {
            type: Array,
            required: true
        },
        oldestDateByMeterSerial: {
            type: Object,
            required: true
        },
        timezone: {
            type: [String, null],
            required: true
        },
    },
    data() {
        return {
            sourceId: null,
            destinationId: null,
            endingTime: null,

            submittingDeviceReplacer: false,
            errorMessage: null,
            showReplacerSuccess: false,
            minimumTime: null,
            archiveExistingData: false,
        }
    },
    created() {
        if (_.isNil(this.timezone)) {
            this.timezone = LuxonDatetime.local().zoneName;
        }
    },
    mounted() {

    },
    methods: {
        submitForm() {
            this.submittingDeviceReplacer = true
            axios.post(this.company.buildCompanyApiInternalRoute('device-replacer'), {
                source_meter: this.sourceId,
                destination_meter: this.destinationId,
                date: this.endingTime,
                archive_existing_data: this.archiveExistingData,
            }).then((response) => {
                this.showSuccessDeviceReplacer()
            }).catch((error) => {
                this.errorMessage = error.response.data.message
            }).then(() => {
                // Always
                this.submittingDeviceReplacer = false
            });
        },
        showSuccessDeviceReplacer() {
            this.showReplacerSuccess = true
        },
        clearDeviceReplacerSettings() {
            this.errorMessage = null
            this.showReplacerSuccess = false
            this.sourceId = null
            this.destinationId = null
            this.endingTime = null
        },
        clearErrorMessage() {
            this.errorMessage = null
        }
    },
    computed: {
        sourceOldestDate() {
            if (_.isNil(this.sourceId) || _.isNil(this.timezone)) {
                return "";
            }

            let date = this.oldestDateByMeterSerial[this.sourceId]
            // If the date is null, return no data exists
            if (_.isNil(date)) {
                return "No data available on point."
            }

            // Since the data exists, adjust it to the site's timezone and display it.
            date = LuxonDatetime.fromISO(date).setZone(this.timezone)
            return date.toLocaleString(LuxonDatetime.DATETIME_FULL);
        },
        destinationOldestDate() {
            if (_.isNil(this.destinationId) || _.isNil(this.timezone)) {
                return "";
            }

            let date = this.oldestDateByMeterSerial[this.destinationId]
            // If the date is null, return no data exists
            if (_.isNil(date)) {
                return "No data available on point."
            }

            date = LuxonDatetime.fromISO(date).setZone(this.timezone)
            return date.toLocaleString(LuxonDatetime.DATETIME_FULL);
        },
        minDate() {
            if (_.isNil(this.sourceId)) {
                return null;
            }

            let date = this.oldestDateByMeterSerial[this.sourceId]
            date = LuxonDatetime.fromISO(date).setZone(this.timezone)
            return date.toJSDate()
        }
    },
    watch: {
        sourceId(val, old) {
            if (val === old) {
                return
            }

            this.clearErrorMessage()
            if (!_.isNil(val)) {
                this.minimumTime = LuxonDatetime.fromISO(this.oldestDateByMeterSerial[this.sourceId]);
            }
        },
        destinationId(val, old) {
            if (val === old) {
                return
            }

            this.clearErrorMessage()
        },
        endingTime(val, old) {
            if (val === old) {
                return
            }

            this.clearErrorMessage()
        },
        showReplacerSuccess(val, old) {
            // If we go from showing the success pop-up to not showing it, clear the form
            if (old && !val) {
                this.clearDeviceReplacerSettings()
            }
        },
    },
}
</script>

<style scoped>

</style>